import { type Dispatch } from 'redux'
import { isAxiosError } from 'axios'
import * as Sentry from '@sentry/browser'
import { type PuzzleType } from 'client/types/PuzzleType'
import type { Level, IPuzzleCalendar } from 'client/interfaces/IPuzzleCalendar'
import { type PuzzleSettings } from 'client/types/PuzzleSettings'
import type { DailyDose, DailyDoseType } from 'client/types/DailyDose'
import { type FinishedPuzzle } from 'client/types/FinishedPuzzle'
import * as uiActions from 'common_packages/assets/js/actions/uiActions'
import { type TDispatch } from 'common_packages/assets/js/types/TDispatch'
import { rethrowError } from 'client/utilities/error'
import { toastTypes } from 'common_packages/assets/js/types/toastTypes'
import { type StoreState } from 'client/store'
import type { TodaysPuzzleState } from 'client/types/TodaysPuzzleState'
import { type ContinuePlayingPuzzleType } from '../types/ContinuePlayingPuzzleType'
import { type FeaturedPuzzleType } from '../types/FeaturedPuzzleType'
import { type ApiResult, klubbleAPI } from '../api/common'
import type { puzzleSortingEnum } from '../enums/puzzleData'
import { saveSettings, saveSettingsBatch } from '../api/puzzleData'
import type {
  PuzzleDataAction,
  PuzzlePacksData,
  SetFavoritePuzzlePackTypePayload,
  SetSelectedLevelPuzzlePackTypePayload,
} from './types'
import { showUpgradeDrawer } from './uiActions'

/* Puzzle types */
export const SET_PUZZLE_TYPES = 'SET_PUZZLE_TYPES'
export const LOADING_CONTINUE_PLAYING_PUZZLE_TYPES = 'LOADING_CONTINUE_PLAYING_PUZZLE_TYPES'
export const SET_CONTINUE_PLAYING_PUZZLE_TYPES = 'SET_CONTINUE_PLAYING_PUZZLE_TYPES'
export const LOADING_FEATURED_PUZZLES = 'LOADING_FEATURED_PUZZLES'
export const SET_FEATURED_PUZZLES = 'SET_FEATURED_PUZZLES'
export const SET_TODAYS_PUZZLES_STATE = 'SET_TODAYS_PUZZLES_STATE'

/* Puzzle packs */
export const SET_PUZZLE_PACKS = 'SET_PUZZLE_PACKS'
export const SET_FAVORITE_PUZZLE_PACK_TYPE = 'SET_FAVORITE_PUZZLE_PACK_TYPE'
export const SET_SELECTED_LEVEL_PUZZLE_PACK_TYPE = 'SET_SELECTED_LEVEL_PUZZLE_PACK_TYPE'

/* Puzzle calendar */
export const SET_PUZZLE_CALENDAR = 'SET_PUZZLE_CALENDAR'
export const SET_PUZZLE_SORTING = 'SET_PUZZLE_SORTING'
export const SAVE_PUZZLE_SETTINGS = 'SAVE_PUZZLE_SETTINGS'
export const SAVE_PUZZLE_SETTINGS_BATCH = 'SAVE_PUZZLE_SETTINGS_BATCH'

/* Puzzle events */
export const SET_FINISHED_PUZZLE = 'SET_FINISHED_PUZZLE'

/* Daily doses */
export const SET_DAILY_DOSES = 'SET_DAILY_DOSES'
export const SET_DAILY_DOSE_TYPES = 'SET_DAILY_DOSE_TYPES'
export const SET_DAILY_DOSE_SELECTED_DATE = 'SET_DAILY_DOSE_SELECTED_DATE'

const GENERAL_API_ERROR_MESSAGE = 'User most likely not authenticated, resulting in API returning 4xx.'
/* eslint no-console: ["error", { allow: ["error"] }] */

export const getPuzzleTypes = () => async (dispatch: TDispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.get<PuzzleType[]>('Puzzle/getpuzzletypes')
    if (res?.status === 200) {
      const puzzleTypes = res.data
      dispatch({
        type: SET_PUZZLE_TYPES,
        payload: puzzleTypes,
      })
    } else {
      throw new Error(`Response returned with status ${res?.status}`)
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const getPuzzlePacks = (abortController: AbortController) => async (dispatch: Dispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.get<ApiResult<PuzzlePacksData>>('Puzzle/packs', {
      signal: abortController.signal,
    })
    if (res?.status === 200) {
      const puzzlePacksData = res.data.data
      dispatch({
        type: SET_PUZZLE_PACKS,
        payload: puzzlePacksData,
      })
    } else {
      throw new Error(`Response returned with status ${res?.status}`)
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const getFeaturedPuzzleTypes = () => async (dispatch: Dispatch<PuzzleDataAction>) => {
  dispatch({
    type: LOADING_FEATURED_PUZZLES,
    payload: true,
  })

  try {
    const res = await klubbleAPI.get<FeaturedPuzzleType[]>('Puzzle/getfeatured')
    if (res?.status === 200) {
      const featuredPuzzleTypes = res.data
      dispatch({
        type: SET_FEATURED_PUZZLES,
        payload: featuredPuzzleTypes,
      })
    } else {
      throw new Error(`Response returned with status ${res?.status}`)
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  } finally {
    dispatch({
      type: LOADING_FEATURED_PUZZLES,
      payload: false,
    })
  }
}

export const getContinuePlayingPuzzleTypes = () => async (dispatch: Dispatch<PuzzleDataAction>) => {
  dispatch({
    type: LOADING_CONTINUE_PLAYING_PUZZLE_TYPES,
    payload: true,
  })

  try {
    const res = await klubbleAPI.get<ContinuePlayingPuzzleType[]>('Puzzle/getcontinueplaying')
    if (res?.status === 200) {
      const continuePlayingPuzzleTypes = res.data
      dispatch({
        type: SET_CONTINUE_PLAYING_PUZZLE_TYPES,
        payload: continuePlayingPuzzleTypes,
      })
    } else {
      throw new Error(`Response returned with status ${res?.status}`)
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  } finally {
    dispatch({
      type: LOADING_CONTINUE_PLAYING_PUZZLE_TYPES,
      payload: false,
    })
  }
}

export const getPuzzleCalendar = (puzzleTypeId: number) => async (dispatch: Dispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.post<IPuzzleCalendar>('Puzzle/getpuzzlecalendar', JSON.stringify({
      puzzleTypeId,
    }), {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (res?.status === 200) {
      const currentPuzzleCalendar = res.data
      dispatch({
        type: SET_PUZZLE_CALENDAR,
        payload: currentPuzzleCalendar,
      })
    } else {
      throw new Error(`Response returned with status ${res?.status}`)
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const getDailyDoses = () => async (dispatch: Dispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.get<ApiResult<{ days: DailyDose[] }>>('Puzzle/dailydosesyear')
    if (res?.data?.success) {
      const dailyDoses = res.data.data?.days.sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
      dispatch({
        type: SET_DAILY_DOSES,
        payload: dailyDoses,
      })
    } else {
      throw new Error('Response not returned successfully')
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const getDailyDoseTypes = () => async (dispatch: Dispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.get<ApiResult<DailyDoseType[]>>('Puzzle/dailydosetypes')
    if (res?.data?.success) {
      const dailyDoseTypes = res.data.data
      dispatch({
        type: SET_DAILY_DOSE_TYPES,
        payload: dailyDoseTypes,
      })
    } else {
      throw new Error('Response not returned successfully')
    }
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const getTodaysPuzzlesStateData = (puzzleTypesAndLevels: { puzzleTypeId: number; level: Level }[], abortController: AbortController) => async (dispatch: Dispatch<PuzzleDataAction>) => {
  try {
    const res = await klubbleAPI.post<TodaysPuzzleState[]>(
      '/Puzzle/gettodayspuzzlestate',
      puzzleTypesAndLevels,
      { signal: abortController.signal },
    )
    if (res.status !== 200 && !res.data.length) {
      throw new Error(`Error getting today's puzzle data for puzzles: ${puzzleTypesAndLevels}`)
    }

    dispatch({
      type: SET_TODAYS_PUZZLES_STATE,
      payload: res.data,
    })
  } catch (e) {
    rethrowError(e, GENERAL_API_ERROR_MESSAGE)
  }
}

export const setFinishedPuzzle = (finishedPuzzle: FinishedPuzzle) => (dispatch: Dispatch<PuzzleDataAction>) => {
  dispatch({
    type: SET_FINISHED_PUZZLE,
    payload: finishedPuzzle,
  })
}

export const setPuzzleSorting = (sort: puzzleSortingEnum) => (dispatch: Dispatch<PuzzleDataAction>) => {
  dispatch({
    type: SET_PUZZLE_SORTING,
    payload: sort,
  })
}

export const savePuzzleSettings = (settings: PuzzleSettings) => (dispatch: TDispatch<PuzzleDataAction>, getState: () => StoreState) => {
  const original = getState().puzzleData.puzzleTypes.find((pT) => pT.puzzleTypeId === settings.puzzleTypeId)
  dispatch({
    type: SAVE_PUZZLE_SETTINGS,
    payload: settings,
  })

  return saveSettings(settings)
    .catch((e) => {
      dispatch({
        type: SAVE_PUZZLE_SETTINGS,
        payload: original,
      })

      dispatch(uiActions.addToast({
        message: e.message,
        type: toastTypes.ERROR,
      }))
      Sentry.captureException(e)
    })
}

export const savePuzzleSettingsBatch = (settings: PuzzleSettings[]) => (dispatch: TDispatch<PuzzleDataAction>, getState: () => StoreState) => {
  const original = getState().puzzleData.puzzleTypes
  dispatch({
    type: SAVE_PUZZLE_SETTINGS_BATCH,
    payload: settings,
  })

  return saveSettingsBatch(settings)
    .catch((e) => {
      dispatch({
        type: SAVE_PUZZLE_SETTINGS_BATCH,
        payload: original,
      })
      dispatch(uiActions.addToast({
        message: e.message,
        type: toastTypes.ERROR,
      }))
      Sentry.captureException(e)
    })
}

export const setFavoritePuzzlePackType = (payload: SetFavoritePuzzlePackTypePayload) => async (dispatch: TDispatch<PuzzleDataAction>) => {
  try {
    // optimistic update
    dispatch({
      type: SET_FAVORITE_PUZZLE_PACK_TYPE,
      payload,
    })
    const url = `/Puzzle/puzzlepacktypes/${payload.puzzlePackTypeId}/favorite/${payload.isFavorite}`
    const res = await klubbleAPI.post(url)
    if (!res.data.success) {
      throw new Error(`${url} did not return succesfully`)
    }
  } catch (e) {
    // revert optimistic update
    dispatch({
      type: SET_FAVORITE_PUZZLE_PACK_TYPE,
      payload: {
        puzzlePackTypeId: payload.puzzlePackTypeId,
        isFavorite: !payload.isFavorite,
      },
    })
    if (isAxiosError(e) && e.response.status === 403) {
      dispatch(showUpgradeDrawer())
    } else {
      dispatch(uiActions.addToast({
        message: e.message,
        type: toastTypes.ERROR,
      }))
      Sentry.captureException(e)
    }
  }
}

export const setSelectedLevelPuzzlePackType = (payload: SetSelectedLevelPuzzlePackTypePayload) => async (dispatch: TDispatch<PuzzleDataAction>) => {
  try {
    const url = `/Puzzle/puzzlepacktypes/${payload.puzzlePackTypeId}/level/${payload.selectedLevel}`
    const res = await klubbleAPI.post(url)
    if (!res.data.success) {
      throw new Error(`${url} did not return succesfully`)
    }
    dispatch({
      type: SET_SELECTED_LEVEL_PUZZLE_PACK_TYPE,
      payload,
    })
  } catch (e) {
    dispatch(uiActions.addToast({
      message: e.message,
      type: toastTypes.ERROR,
    }))
    Sentry.captureException(e)
  }
}

export const setDailyDoseSelectedDate = (date: string) => (dispatch: Dispatch<PuzzleDataAction>) => {
  dispatch({
    type: SET_DAILY_DOSE_SELECTED_DATE,
    payload: date,
  })
}
